import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WelcomeScreen from './WelcomeScreen';
import GroupBasics from './GroupBasics';
import ContributionDetails from './ContributionDetails';
import StartDate from './StartDate';
import SuccessScreen from './SuccessScreen';
import { supabaseClient } from '../../../shared/utils/supabaseClient';

const OnboardingWizard = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const totalSteps = 5;

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleNext = (stepData) => {
    updateFormData(stepData);
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async (finalData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get authenticated user before group creation
      const { data: { user }, error: authError } = await supabaseClient.auth.getUser();
      if (authError || !user) {
        console.error('Auth error:', authError);
        throw new Error('Not authenticated');
      }
      const completeData = { ...formData, ...finalData };
      
      // Create group in Supabase
      const insertPayload = {
        group_name: completeData.groupName,
        cultural_term: completeData.culturalTerm,
        contribution_amount_per_hand: completeData.amount,
        contribution_frequency: completeData.frequency,
        start_date: completeData.startDate,
        // Money-flow model (Stage 24 / Phase C1). Defaults to through_admin; the DB
        // column also defaults it, so omitting it is safe.
        money_flow_model: completeData.moneyFlowModel || 'through_admin',
        status: 'active',
        // A new group correctly starts at cycle 1. Round 1 is seeded by the cycle
        // engine (open_first_round) once hands are assigned — NOT here — because
        // seeding requires the roster to exist. This is the group's initial cycle,
        // not a hardcoded payment/payout cycle_number.
        cycle_number: 1,
        subscription_tier: 'free',
        billing_admin_id: user.id
      };
      
      const { data: group, error: groupError } = await supabaseClient
        .from('groups')
        .insert(insertPayload)
        .select()
        .single();

      
      if (groupError) {
        console.error('❌ Group creation failed:', groupError);
        throw groupError;
      }

      try {
        await supabaseClient.from('audit_logs').insert({
          event_category: 'admin_action',
          event_type: 'group_created',
          actor_id: user.id,
          actor_role: 'admin',
          group_id: group.id,
          new_value: {
            group_name: completeData.groupName,
            cultural_term: completeData.culturalTerm,
            contribution_amount_per_hand: completeData.amount,
            contribution_frequency: completeData.frequency,
            start_date: completeData.startDate
          },
          risk_level: 'low',
          flagged: false,
          timestamp: new Date().toISOString()
        });
      } catch (auditError) {
        console.error('Failed to log group creation:', auditError);
      }

      
      setCurrentStep(totalSteps);
      updateFormData({ groupId: group.id });
      setIsLoading(false);
      
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Failed to create group. Please try again.');
      setIsLoading(false);
    }
  };

  const handleInviteMembers = () => {
    onComplete?.(formData);
  };

  const renderStep = () => {
    const stepProps = {
      data: formData,
      onNext: currentStep === 4 ? handleComplete : handleNext,
      onBack: handleBack
    };

    switch (currentStep) {
      case 1:
        return <WelcomeScreen onNext={() => handleNext()} />;
      case 2:
        return <GroupBasics {...stepProps} />;
      case 3:
        return <ContributionDetails {...stepProps} />;
      case 4:
        return <StartDate {...stepProps} />;
      case 5:
        return <SuccessScreen 
  data={formData} 
  groupId={formData.groupId}
  onInvite={(path) => navigate(path)}
  onSkip={() => navigate(`/admin/group/${formData.groupId}/payments`)}
/>;
      default:
        return <WelcomeScreen onNext={() => handleNext()} />;
    }
  };

  const renderProgressIndicator = () => {
    if (currentStep === 1 || currentStep === 5) return null;

    return (
      <div className="flex justify-center space-x-2 mb-6">
        {Array.from({ length: totalSteps - 1 }, (_, index) => (
          <div
            key={index + 1}
            className={`h-2 w-8 rounded-full transition-colors ${
              index + 1 < currentStep
                ? 'bg-primary-600'
                : index + 1 === currentStep
                ? 'bg-primary-600'
                : 'bg-neutral-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {renderProgressIndicator()}
      
      {error && (
        <div className="mx-4 mb-4 p-4 bg-error-50 border border-error-200 rounded-lg">
          <p className="text-sm text-error-800">{error}</p>
        </div>
      )}
      
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-neutral-100 rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="text-neutral-700">Creating your group...</span>
          </div>
        </div>
      )}
      
      {renderStep()}
    </div>
  );
};

export default OnboardingWizard;
